import * as Sentry from '@sentry/browser';
import ApplicationModel, { attr } from '../ApplicationModel';
import type Query from '../Query';
import { type Selection } from '../Query';
import DeliveryRiskFilter, {
  type DeliveryRiskType,
} from '../filters/DeliveryRiskFilter';
import { VISIBLE_FILTERS_KEY } from '../filters/DevelopBoardFilters';
import EpicFilter from '../filters/EpicFilter';
import EstimateNumericFilter from '../filters/EstimateNumericFilter';
import FeatureTypeFilter from '../filters/FeatureTypeFilter';
import type Filter from '../filters/Filter';
import ItemFilter from '../filters/ItemFilter';
import { type NumericConfigComparison } from '../filters/NumericFilter';
import type RecordFilter from '../filters/RecordFilter';
import RecordTypeFilter from '../filters/RecordTypeFilter';
import TagFilter from '../filters/TagFilter';
import UserFilter from '../filters/UserFilter';
import WorkflowIterationFilter from '../filters/WorkflowIterationFilter';
import WorkflowStatusAgeFilter from '../filters/WorkflowStatusAgeFilter';
import { model } from '../modelBuilder';
import BookmarksRecordPosition from './BookmarksRecordPosition';
import BookmarksTeamBacklog from './BookmarksTeamBacklog';
import CardLayout from './CardLayout';
import CustomFieldValue from './CustomFieldValue';
import Epic from './Epic';
import Feature from './Feature';
import Iteration from './Iteration';
import Project from './Project';
import ProjectConfiguration from './ProjectConfiguration';
import Requirement from './Requirement';
import Tag from './Tag';
import User from './User';
import Workflow from './Workflow';
import WorkflowKind from './WorkflowKind';
import WorkflowStatus from './WorkflowStatus';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { HasBookmarkSidebarSettings } from './concerns/HasBookmarkSidebarSettings';
import { type BookmarksWorkflowBoardAttributes } from './generated/BookmarksWorkflowBoardAttributes';

export const BACKLOG_RELEASE_SCOPE = [
  'id',
  'name',
  'releaseDate',
  'parkingLot',
  'position',
];

const COMMON_FIELDS = ['id', 'referenceNum', 'name', 'path'];

/**
 * Map of visible filter items to their required fields.
 * This allows us to conditionally include expensive fields only when needed.
 */
const FILTER_REQUIRED_FIELDS: Record<
  string,
  { objectTypes: string[]; select: string[] }
> = {
  delivery_risk: {
    objectTypes: ['Feature', 'Requirement'],
    select: ['riskFactors', 'riskFactorsCount'],
  },
};

const ONE_DAY = 86400;

export type BookmarkFilters = {
  users: UserFilter;
  tags: TagFilter;
  epics: EpicFilter;
  days_in_team_workflow_status: WorkflowStatusAgeFilter;
  record_type: RecordTypeFilter;
  feature_type: FeatureTypeFilter;
  iterations: WorkflowIterationFilter;
  delivery_risk: DeliveryRiskFilter;
  visibleFilters: ItemFilter<any>;
  initialEstimate: EstimateNumericFilter;
  originalEstimate: EstimateNumericFilter;
};

export type BookmarkFilterKey = keyof BookmarkFilters;

type WorkflowBoardFilterValues = {
  users: RecordId[];
  tags: RecordId[];
  record_type: string[];
  feature_type: string[];
  iterations: RecordId[];
  days_in_team_workflow_status?: NumericConfigComparison;
  delivery_risk: DeliveryRiskType[];
  visibleFilters: string[];
  initialEstimate: NumericConfigComparison;
  originalEstimate: NumericConfigComparison;
};

interface BookmarksWorkflowBoard
  extends BookmarksWorkflowBoardAttributes,
    HasBookmarkFilters<BookmarkFilters>,
    HasBookmarkSidebarSettings {
  filters: WorkflowBoardFilterValues;
}

@model
@HasBookmarkFilters
@HasBookmarkSidebarSettings
class BookmarksWorkflowBoard extends ApplicationModel {
  static OBJECT_CLASSES = [Feature, Requirement];

  // Start auto-generated
  static typename: 'BookmarksWorkflowBoard' = 'BookmarksWorkflowBoard';
  get typename(): 'BookmarksWorkflowBoard' {
    return 'BookmarksWorkflowBoard';
  }

  static internalTypename: 'Bookmarks::WorkflowBoard' =
    'Bookmarks::WorkflowBoard';
  get internalTypename(): 'Bookmarks::WorkflowBoard' {
    return 'Bookmarks::WorkflowBoard';
  }

  static bookmarkName = 'Bookmarks::WorkflowBoard';

  static inputType = 'WorkflowBoardAttributes!';

  static fields = {
    collapsedColumns: attr({ writable: true, readable: true }),
    completedLimit: attr({ writable: true, readable: true }),
    filters: attr({ writable: true, readable: true, json: true }),
    group: attr({ writable: true, readable: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    showCollapsedView: attr({ writable: true, readable: true }),
    showEmptySwimlanes: attr({ writable: true, readable: true }),
    sidebarSettings: attr({ writable: true, readable: true, json: true }),
    view: attr({ writable: true, readable: true }),
    workflowId: attr({ readable: true }),
    cardLayout: attr.belongsTo({
      writeShape: [
        'epicRecordLayout',
        'featureRecordLayout',
        'ideaRecordLayout',
        'initiativeRecordLayout',
        'releaseRecordLayout',
        'releasePhaseRecordLayout',
        'requirementRecordLayout',
        'taskRecordLayout',
        'goalRecordLayout',
        'keyResultRecordLayout',
      ],
      readable: true,
      typename: 'CardLayout',
    }),
    defaultCardLayout: attr.belongsTo({
      readable: true,
      typename: 'CardLayout',
    }),
    iteration: attr.belongsTo({ readable: true, typename: 'Iteration' }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    workflow: attr.belongsTo({ readable: true, typename: 'Workflow' }),
    allRecords: attr.hasMany({
      args: { filters: 'RecordFilters' },
      readable: true,
    }),
    records: attr.hasMany({
      args: { filters: 'RecordFilters' },
      readable: true,
    }),
  };
  // End auto-generated

  initialize() {
    this.local = {
      workflow: null,
      users: [],
      epics: [],
      features: [],
      filterObjects: {},
      unfilteredRecordIds: new Set(),
    };
  }

  static buildWorkflowBoardScope(loadCardLayout = false) {
    let scope = this.select([
      'id',
      'projectId',
      'workflowId',
      'view',
      'group',
      'showCollapsedView',
      'collapsedColumns',
      'completedLimit',
      'filters',
      'sidebarSettings',
      'showEmptySwimlanes',
    ]).merge({
      iteration: ['id', 'name', 'status'],
      project: Project.select([
        'id',
        'backlogManagementEnabled',
        'backlogBookmarkId',
        'epicsEnabled',
        'iterationsEnabled',
        'referencePrefix',
      ]).merge({
        wipLimits: ['workflowStatusId', 'limit'],
      }),
      workflow: this.defaultWorkflowScope(),
    });

    if (loadCardLayout) {
      scope = scope.merge({
        cardLayout: CardLayout.buildCardLayoutScope({
          recordTypes: ['Epic', 'Feature', 'Requirement'],
        }),
        defaultCardLayout: CardLayout.buildCardLayoutScope({
          recordTypes: ['Epic', 'Feature', 'Requirement'],
        }),
      });
    }

    return scope;
  }

  static defaultWorkflowScope() {
    return Workflow.select(['id']).merge({
      workflowStatuses: WorkflowStatus.select([
        'id',
        'name',
        'color',
        'position',
        'internalMeaning',
      ]),
      workflowKinds: WorkflowKind.select(['id', 'name', 'iconName', 'color']),
    });
  }

  get defaultUser() {
    return User.blank({
      name: 'Unassigned',
    });
  }

  /**
   * Build the default record scope to load all workable items for this team/iteration.
   */
  buildDefaultRecordScope(
    options: {
      cardLayout?: CardLayout;
      customFieldKeys?: any[];
      showCollapsedView?: boolean;
    } = {}
  ) {
    const scopes = BookmarksWorkflowBoard.OBJECT_CLASSES.map(objectClass =>
      this.buildRecordScope(objectClass, options)
    ).reduce((acc, scope) => acc.union(scope));

    return scopes;
  }

  /**
   * Return a scope that can be used to find records belonging to a workflow board board.
   */
  buildRecordScope(
    objectClass: typeof ApplicationModel,
    options: {
      cardLayout?: CardLayout;
      customFieldKeys?: any[];
      showCollapsedView?: boolean;
    } = {}
  ) {
    const { cardLayout } = options;
    const recordLayout = cardLayout?.recordLayout(objectClass.typename);

    const includes: Selection = {
      teamWorkflowStatus: WorkflowStatus.select([
        'id',
        'name',
        'internalMeaning',
      ]).first(),
      assignedToUser: User.select([
        'id',
        'name',
        'avatarUrl',
        'initials',
      ]).first(),
      bookmarksRecordPositions: this.recordPositionScope(),
      team: ['id'],
      iteration: ['id', 'status'],
    };

    if (objectClass.hasField('epic')) {
      includes.epic = Epic.select(COMMON_FIELDS).first();
    }

    if (objectClass.hasField('initialEstimate')) {
      includes.initialEstimate = ['value', 'text', 'units'];
    }

    if (objectClass.hasField('originalEstimate')) {
      includes.originalEstimate = ['value', 'text', 'units'];
    }

    if (objectClass.typename === 'Requirement') {
      includes.feature = Feature.select(COMMON_FIELDS)
        .merge({
          release: BACKLOG_RELEASE_SCOPE,
        })
        .first();
    }

    if (objectClass.hasField('release')) {
      includes.release = BACKLOG_RELEASE_SCOPE;
    }

    if (objectClass.hasField('project')) {
      includes.project = Project.select(['id', 'name', 'isTeam'])
        .first()
        .merge({
          projectConfiguration: ProjectConfiguration.merge({
            projectRisksConfiguration: ['enabled', 'manuallyFlagged'],
          }),
        });
    }

    if (objectClass.hasField('tags')) {
      includes.tags = ['id', 'name', 'color'];
    }

    if (objectClass.hasField('workflowKind')) {
      includes.workflowKind = WorkflowKind.select(['id', 'name']);
    }

    if (objectClass.typename === 'Feature' && options.customFieldKeys?.length) {
      includes.customFieldValues = CustomFieldValue.select([
        'key',
        'value',
        'position',
      ]).argument({ keys: options.customFieldKeys });
    }

    const select = [
      ...COMMON_FIELDS,
      'position',
      'commentsCount',
      'durationInTeamWorkflowStatus',
      'manualRiskComment',
    ];

    // Include fields required by active filters
    const visibleFilters = this.getFilter('visibleFilters');
    if (visibleFilters) {
      for (const [filterKey, config] of Object.entries(
        FILTER_REQUIRED_FIELDS
      )) {
        if (
          visibleFilters.itemIsSelected(filterKey) &&
          config.objectTypes.includes(objectClass.typename)
        ) {
          select.push(...config.select);
        }
      }
    }

    let query = objectClass.select(select).merge(includes);

    if (recordLayout) {
      const cardView = options.showCollapsedView
        ? recordLayout.collapsed
        : recordLayout.expanded;

      if (cardView) {
        query = cardView.buildScope(query);
      } else {
        Sentry.captureMessage('No cardview for record layout', 'warning');
      }
    }

    return query;
  }

  recordPositionScope() {
    return BookmarksRecordPosition.select([
      'id',
      'position',
      'bookmarkType',
      'secondaryScope',
    ]).where({
      bookmarkType: [
        BookmarksTeamBacklog.bookmarkName,
        BookmarksWorkflowBoard.bookmarkName,
      ],
    });
  }

  async loadRecords({ customFieldKeys }) {
    const query = BookmarksWorkflowBoard.merge({
      records: this.buildDefaultRecordScope({
        customFieldKeys,
        cardLayout: this.activeCardLayout,
      }),
    });

    const withRecords = await query.find(this.id);

    // Set the raw records attribute value
    this.attributes.records = withRecords.attributes.records;

    return this.records;
  }

  get activeCardLayout() {
    const { cardLayout, defaultCardLayout } = this;

    if (cardLayout && defaultCardLayout) {
      return CardLayout.buildActiveCardLayout({
        cardLayout,
        defaultCardLayout,
      });
    }
  }

  /**
   * `true` if the record can show on this board, 'false' otherwise.
   */
  canContainRecord(record) {
    if (record.team?.id === this.projectId) {
      if (this.iteration) {
        return (
          record.iteration &&
          record.iteration.status !== Iteration.STATUS_COMPLETED
        );
      }

      if (this.project.backlogManagementEnabled) {
        return record.recordPosition(BookmarksWorkflowBoard.bookmarkName);
      }

      return true;
    }
    return false;
  }

  /**
   * Return `true` if the specified workflowStatus can be deleted,
   * `false` otherwise.
   */
  canDeleteStatus(workflowStatus) {
    return (
      !workflowStatus.builtIn &&
      !this.records.some(
        record => record.teamWorkflowStatus?.id === workflowStatus.id
      )
    );
  }

  get workflowStatuses() {
    return this.workflow ? this.workflow.sortedWorkflowStatuses : [];
  }

  get wipLimits() {
    const { wipLimits } = this.project;

    return wipLimits.reduce((acc, wipLimit) => {
      acc[wipLimit.workflowStatusId] = wipLimit.limit;
      return acc;
    }, {});
  }

  addWorkflowStatus(newStatus) {
    this.workflow.addWorkflowStatus(newStatus);
  }

  insertWorkflowStatusAtIndex(status, index) {
    this.workflow.insertWorkflowStatusAtIndex(status, index);
  }

  removeWorkflowStatus(status) {
    this.workflow.removeWorkflowStatus(status);
  }

  get completedLimitDays() {
    return this.completedLimit / ONE_DAY;
  }

  set completedLimitDays(value) {
    this.completedLimit = value * ONE_DAY;
  }

  canFilter(scope: keyof BookmarkFilters) {
    if (scope === 'iterations' && !this.project.iterationsEnabled) return false;
    if (scope === 'epics' && !this.project.epicsEnabled) return false;

    return !!this.getFilter(scope) && scope !== 'visibleFilters';
  }

  /**
   * Returns a Filter subclass initialized with the active set of
   * filters. This is used, for example, to fetch user names when
   * there are active user filters, so we can display them in the
   * dropdown.
   *
   * @param filterScope The key for the filter hash stored in the workflow board config. For example, "users" or "epics"
   * @param recordScope A Query used to load filter information from the server
   * @param FilterClass The Filter subclass to initialize with the retrieved data
   * @param extraRecords Any extra records to append to the list. "Unassigned," for example.
   * @param filterParams Any extra options to supply to the filter class
   */
  async loadFilter<
    F extends Filter,
    ExtraRecord extends { id: string; name: string }
  >(
    filterScope: keyof BookmarkFilters,
    recordScope: Query<any, any>,
    FilterClass: { new (...args: any[]): F },
    extraRecords: ExtraRecord[] = [],
    filterParams = {}
  ) {
    const filteredIds = this.filters[filterScope] || [];
    let filteredRecords: ExtraRecord[] = [];

    if (filteredIds.length > 0) {
      filteredRecords = await ApplicationModel.fetchAll(
        recordScope.where({ id: filteredIds })
      );
    }

    const filteredOptions = filteredRecords
      .concat(extraRecords)
      .map(t => [t.name, t.id || '']);

    // Exclude any selection not returned by the fetch - they are
    // no longer a valid selection
    const selectedIds = filteredOptions
      .filter(option => filteredIds.includes(option[1]))
      .map(option => option[1]);

    return new FilterClass(selectedIds, filteredOptions, filterParams);
  }

  /**
   * Loads filter data for all possible filter types.
   */
  async createFilters(): Promise<BookmarkFilters> {
    const userFilterPromise = this.loadFilter(
      'users',
      User.select(['id', 'name']).where({ projectId: this.projectId }),
      UserFilter,
      [this.defaultUser]
    );
    const tagFilterPromise = this.loadFilter(
      'tags',
      Tag.select(['id', 'name']),
      TagFilter,
      []
    );
    const epicFilterPromise = this.loadFilter(
      'epics',
      Epic.select(['id', 'referenceNum', 'name']).where({
        projectId: this.projectId,
      }),
      EpicFilter,
      []
    );

    const currentIteration =
      this.iteration?.status === Iteration.STATUS_STARTED
        ? this.iteration
        : null;

    const iterationFilterPromise = this.loadFilter(
      'iterations',
      Iteration.select(['id', 'name']).where({
        projectId: this.projectId,
        status: [Iteration.STATUS_PLANNING, Iteration.STATUS_STARTED],
      }),
      WorkflowIterationFilter,
      [],
      { currentIterationId: currentIteration?.id }
    );

    const recordType = new RecordTypeFilter({
      initialItems: this.filters?.record_type ?? [],
      recordTypes: ['Feature', 'Requirement'],
    });

    const featureType = new FeatureTypeFilter(
      this.filters?.feature_type ?? [],
      this.workflow.workflowKinds?.map(kind => [kind.name, kind.name])
    );

    const daysInTeamWorkflowStatus = new WorkflowStatusAgeFilter(
      this.filters?.days_in_team_workflow_status
    );

    const deliveryRisk = new DeliveryRiskFilter(
      this.filters?.delivery_risk ?? []
    );

    const initialEstimate = new EstimateNumericFilter(
      this.filters?.initialEstimate,
      'initialEstimate'
    );
    const originalEstimate = new EstimateNumericFilter(
      this.filters?.originalEstimate,
      'originalEstimate'
    );

    const existing =
      this.filters?.visibleFilters ||
      Object.entries(
        this.filters as Record<string, any[] | Record<string, any>>
      ).reduce((acc: string[], [key, value]) => {
        if (
          value &&
          // special case to handle the days in team workflow status filter
          (('length' in value && value.length > 0) ||
            (typeof value === 'object' &&
              key === 'days_in_team_workflow_status')) &&
          key !== VISIBLE_FILTERS_KEY
        ) {
          acc.push(key);
        }
        return acc;
      }, []);

    const visibleFilters = new ItemFilter(existing);

    const [userFilter, tagFilter, epicFilter, iterationFilter] =
      await Promise.all([
        userFilterPromise,
        tagFilterPromise,
        epicFilterPromise,
        iterationFilterPromise,
      ]);

    return {
      users: userFilter,
      tags: tagFilter,
      epics: epicFilter,
      iterations: iterationFilter,
      days_in_team_workflow_status: daysInTeamWorkflowStatus,
      record_type: recordType,
      feature_type: featureType,
      delivery_risk: deliveryRisk,
      initialEstimate: initialEstimate,
      originalEstimate: originalEstimate,
      visibleFilters,
    };
  }

  /**
   * Updates the server-side board state with the current filter state.
   */
  writeFilterAttribute(_scope) {
    this.clearUnfiltered();
  }

  canDeleteFilterItem(scope: keyof BookmarkFilters) {
    // @ts-ignore filters are not yet typed
    return this.getFilter(scope).hasItems;
  }

  /**
   * Returns a list of Filter objects that can be used on the active
   * board. This excludes filters that don't make sense to run -- `epic`
   * on the Epics board, for example.
   */
  get activeFilters() {
    return Object.entries(
      this.local.filterObjects as Record<string, RecordFilter>
    )
      .filter(
        ([key, value]) =>
          this.canFilter(key as any) && (value.hasValue || key === 'iterations')
      )
      .map(([key, value]) => value);
  }

  /**
   * Returns any `records` matched by all active filters.
   */
  applyFilters<T>(records: T[]): T[] {
    return records.filter(record => this.isRecordVisible(record));
  }

  /**
   * Returns any `records` matched by `filter`.
   *
   * Will ignore any record marked as "unfiltered", to prevent new
   * records that don't match the active filters from immediately
   * disappearing.
   */
  applyFilter(filter, records) {
    return records.filter(rec => this.unfiltered(rec) || filter.matches(rec));
  }

  /**
   * Returns whether the record is visible (not filtered) on the board or not.
   */
  isRecordVisible(record) {
    if (
      this.view === 'my_work' &&
      record.assignedToUser?.id !== window.currentUser?.id
    ) {
      return false;
    }

    let { activeFilters } = this;

    // Remove the users filter if we're viewing my work
    if (this.view === 'my_work') {
      activeFilters = activeFilters.filter(
        filter => !(filter instanceof UserFilter)
      );
    }

    const isVisible =
      this.unfiltered(record) ||
      activeFilters.every(filter => filter.matches(record));

    return isVisible;
  }

  /**
   * Returns `true` if a record is considered `unfilterable`, false otherwise.
   *
   * When adding a record to a board, it may not match the active set
   * of filters -- for example, if you're filtering by tags, the
   * record will not have the active tags. This means the record would immediately disappear.
   *
   * By flagging a record as `unfilterable`, the record will always
   * appear until the board state changes.
   */
  unfiltered(record) {
    return (
      record.id === undefined || this.local.unfilteredRecordIds.has(record.id)
    );
  }

  /**
   * Flag `record` as unfilterable.
   */
  unfilter(record) {
    if (!this.activeFilters.every(f => f.matches(record))) {
      this.local.unfilteredRecordIds.add(record.id);
    }
  }

  /**
   * Resets the `unfilterable` flag on all records.
   */
  clearUnfiltered() {
    this.local.unfilteredRecordIds.clear();
  }

  /**
   * Checks if changing a filter requires reloading records.
   */
  filterChangeRequiresReload(newFilters: string[]): boolean {
    const reloadFilters = Object.keys(FILTER_REQUIRED_FIELDS);
    const visibleFilters = this.getFilter('visibleFilters');

    for (const filterKey of reloadFilters) {
      const wasActive = visibleFilters?.itemIsSelected(filterKey) ?? false;
      const isActive = newFilters.includes(filterKey) ?? false;

      // Only reload when transitioning from inactive to active
      if (!wasActive && isActive) {
        return true;
      }
    }

    return false;
  }
}

export default BookmarksWorkflowBoard;
